import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ExplorerShell from "../components/explorer/ExplorerShell";
import {
  ProductExplorerCard,
  ProductExplorerListRow,
} from "../components/explorer/ProductExplorerViews";
import {
  UNCATEGORIZED_CATEGORY_ID,
  useExplorerUrlState,
} from "../hooks/useExplorerUrlState";
import { useCategories } from "../hooks/useCategories";
import { apiService } from "../api/services/apiService";
import type { Product } from "../types";
import { EntityStatus } from "../types";
import {
  collectUniqueProductTags,
  compareExplorerValues,
  isVisibleToExplorer,
  matchesExplorerSearch,
  paginate,
  primaryProductCategoryName,
  productHasUncategorizedCategories,
  productMatchesCategoryFilter,
  productMatchesTagFilter,
  EXPLORER_PAGE_SIZE_CARD,
  EXPLORER_PAGE_SIZE_LIST,
} from "../utils/explorerUtils";

const PRODUCT_SORT_OPTIONS = [
  { id: "name", label: "Name" },
  { id: "category", label: "Category" },
  { id: "status", label: "Status" },
];

interface FilterOption {
  id: string;
  name: string;
}

interface FilterCheckboxAccordionProps {
  title: string;
  selectedCount: number;
  loading?: boolean;
  error?: string | null;
  options: FilterOption[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  emptyMessage: string;
  defaultExpanded?: boolean;
}

const accordionSx = {
  mt: 2,
  border: "1px solid",
  borderColor: "grey.200",
  "&:before": { display: "none" },
} as const;

const FilterCheckboxAccordion: React.FC<FilterCheckboxAccordionProps> = ({
  title,
  selectedCount,
  loading = false,
  error = null,
  options,
  selectedIds,
  onToggle,
  onClear,
  emptyMessage,
  defaultExpanded = false,
}) => (
  <Accordion defaultExpanded={defaultExpanded} elevation={0} sx={accordionSx}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant="body2" fontWeight={600}>
        {title}
        {selectedCount > 0 ? ` (${selectedCount} selected)` : ""}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0, maxHeight: 220, overflowY: "auto" }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!loading &&
        options.map((opt) => (
          <FormControlLabel
            key={opt.id}
            control={
              <Checkbox
                size="small"
                checked={selectedIds.includes(opt.id)}
                onChange={() => onToggle(opt.id)}
              />
            }
            label={<Typography variant="body2">{opt.name}</Typography>}
          />
        ))}
      {!loading && options.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}
      {selectedCount > 0 && (
        <Button
          size="small"
          onClick={onClear}
          sx={{ mt: 1, textTransform: "none" }}
        >
          Clear {title.toLowerCase()}
        </Button>
      )}
    </AccordionDetails>
  </Accordion>
);

const ProductExplorerPage: React.FC = () => {
  const state = useExplorerUrlState({ defaultSort: "name" });
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    categories: allCategories,
    loading: loadingCategories,
    error: categoriesError,
  } = useCategories();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.getProducts();
      setProducts(list.filter((p) => isVisibleToExplorer(p.status)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const hasUncategorizedProducts = useMemo(
    () => products.some(productHasUncategorizedCategories),
    [products],
  );

  const categoryFilterOptions = useMemo((): FilterOption[] => {
    const activeCategories = allCategories
      .filter((c) => c.status === EntityStatus.ACTIVE)
      .map((c) => ({ id: c.uuid, name: c.name }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );

    const options: FilterOption[] = [...activeCategories];

    if (hasUncategorizedProducts) {
      options.push({
        id: UNCATEGORIZED_CATEGORY_ID,
        name: "Uncategorized",
      });
    }

    return options;
  }, [allCategories, hasUncategorizedProducts]);

  const tagFilterOptions = useMemo(
    (): FilterOption[] =>
      collectUniqueProductTags(products).map((tag) => ({
        id: tag,
        name: tag,
      })),
    [products],
  );

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categoryFilterOptions.forEach((opt) => map.set(opt.id, opt.name));
    return map;
  }, [categoryFilterOptions]);

  const processed = useMemo(() => {
    let list = products;

    if (state.statuses.length) {
      list = list.filter((p) =>
        state.statuses.includes(p.status as (typeof state.statuses)[number]),
      );
    }

    list = list.filter((p) =>
      productMatchesCategoryFilter(p, state.categoryIds),
    );

    list = list.filter((p) => productMatchesTagFilter(p, state.tags));

    list = list.filter((p) =>
      matchesExplorerSearch(state.debouncedQ, [
        p.name,
        p.description,
        ...(p.categories ?? []).map((c) => c.name),
        ...(p.tags ?? []),
      ]),
    );

    list = [...list].sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (state.sort) {
        case "category":
          av = primaryProductCategoryName(a);
          bv = primaryProductCategoryName(b);
          break;
        case "status":
          av = a.status;
          bv = b.status;
          break;
        default:
          av = a.name;
          bv = b.name;
      }
      return compareExplorerValues(av, bv, state.sortDir);
    });

    return list;
  }, [
    products,
    state.debouncedQ,
    state.sort,
    state.sortDir,
    state.statuses,
    state.categoryIds,
    state.tags,
  ]);

  const pageSize =
    state.layout === "list"
      ? EXPLORER_PAGE_SIZE_LIST
      : EXPLORER_PAGE_SIZE_CARD;
  const paginated = paginate(processed, state.page, pageSize);

  const activeFilterChips = (
    <>
      {state.categoryIds.map((id) => (
        <Chip
          key={`cat-${id}`}
          label={categoryNameById.get(id) ?? id}
          size="small"
          onDelete={() => state.toggleCategoryId(id)}
          sx={{ mr: 0.5, mb: 1 }}
        />
      ))}
      {state.tags.map((tag) => (
        <Chip
          key={`tag-${tag}`}
          label={tag}
          size="small"
          color="primary"
          variant="outlined"
          onDelete={() => state.toggleTag(tag)}
          sx={{ mr: 0.5, mb: 1 }}
        />
      ))}
    </>
  );

  const clearProductFilters = () => {
    state.clearCategoryIds();
    state.clearTags();
  };

  return (
    <ExplorerShell
      pageTitle="Product Explorer"
      pageDescription="Browse and explore products. Filter by category, tag, status, or search."
      loading={loading}
      error={error}
      items={paginated}
      totalFiltered={processed.length}
      state={state}
      sortOptions={PRODUCT_SORT_OPTIONS}
      hasExtraFilters={
        state.categoryIds.length > 0 || state.tags.length > 0
      }
      activeFilterChips={activeFilterChips}
      emptyMessage="No products are available to explore."
      getItemKey={(p) => p.uuid}
      renderCard={(p) => <ProductExplorerCard product={p} />}
      renderListRow={(p) => <ProductExplorerListRow product={p} />}
      onClearAllExtra={clearProductFilters}
      filterSidebarExtra={
        <>
          <FilterCheckboxAccordion
            title="Categories"
            selectedCount={state.categoryIds.length}
            loading={loadingCategories}
            error={categoriesError}
            options={categoryFilterOptions}
            selectedIds={state.categoryIds}
            onToggle={state.toggleCategoryId}
            onClear={state.clearCategoryIds}
            emptyMessage="No categories available to filter by."
            defaultExpanded
          />
          <FilterCheckboxAccordion
            title="Tags"
            selectedCount={state.tags.length}
            options={tagFilterOptions}
            selectedIds={state.tags}
            onToggle={state.toggleTag}
            onClear={state.clearTags}
            emptyMessage="No tags found on products."
          />
        </>
      }
    />
  );
};

export default ProductExplorerPage;
